import { Box, IconButton, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonModal, CustomPagination, FunctionsEnum } from '@watter/shared-components';
import { useDispatch } from 'react-redux';
import { userEnums } from '@watter/shared-types';
import { AntSwitch, CommonTable } from '../../components/common-styles';
import { CustomSlider } from '../../components/slider/slider';
import { client } from '../../service/apolloClient';
import { GET_MANAGE_ADMIN_LIST } from '../../graphql/queries';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { UPDATE_ADMIN } from '../../graphql/mutations';
import { openToast } from '../../store/reducers/toastSlice';
import { CreateAdmin } from './create-admin';
import { getAdminListData } from './helper';
import { AdminData, AdminModel } from '../../interfaces/admin-interface';
import { usePermissions } from '../../custom_hooks/permissions';

declare global {
    interface Window {
        handleAddAdmin?: () => void;
    }
}

export const ManageAdmin: React.FC = () => {
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = useState(false);
    const [removeAdmin, setRemoveAdmin] = useState(false);
    const [listData, setAdminListData] = useState<AdminData[]>([]);
    const [selectedAdmin, setSelectedAdmin] = useState<AdminData | null>(null);
    const [newStatus, setNewStatus] = useState<number | null>(null);
    const dispatch = useDispatch();
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const rowsPerPage = 10;
    const [page, setCurrentPage] = React.useState(1);
    const tableRef = useRef<HTMLInputElement>(null);
    const [openCreatePopUp, setOpenCreatePopUp] = React.useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const handleEditClick = (admin: AdminData, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAdmin(admin);
        setOpenCreatePopUp(true);
        setIsEdit(true);
    };

    const handleStatusToggle = (row: AdminData) => {
        setSelectedAdmin(row);
        setNewStatus(row.status === userEnums.Status.active ? userEnums.Status.inactive : userEnums.Status.active);
        setModalOpen(true);
    };
    const handleAddAdmin = () => {
        setOpenCreatePopUp(true);
    };
    useEffect(() => {
        window.handleAddAdmin = handleAddAdmin;
        return () => {
            delete window.handleAddAdmin;
        };
    }, []);

    const fetchMangeAdminListData = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query<{
                // eslint-disable-next-line no-undef
                getAdminsList: {
                    // eslint-disable-next-line  no-use-before-define
                    data: AdminModel[];
                    // eslint-disable-next-line no-undef
                    total_count: number;
                };
            }>({
                query: GET_MANAGE_ADMIN_LIST,
                variables: {
                    limit: rowsPerPage,
                    page,
                },
                fetchPolicy: 'network-only',
            });

            if (data?.getAdminsList?.data) {
                const formattedRows: AdminData[] = getAdminListData(data?.getAdminsList?.data);
                setTotalRecordCount(data?.getAdminsList?.total_count);
                setAdminListData(formattedRows);
            } else {
                console.warn('No data found in API response');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, rowsPerPage, page]);

    const handleStatusToggleApi = async (admin: any, status: number) => {
        try {
            dispatch(showLoader());

            const input = {
                status,
                first_name: admin.first_name,
                last_name: admin.last_name,
                cognito_id: admin.cognito_id.toString(),
            };

            const { data } = await client.mutate({
                mutation: UPDATE_ADMIN,
                variables: { input },
            });

            await fetchMangeAdminListData();

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateAdmin?.message || t('updated_status_success_message'),
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
        } catch (err) {
            console.error('Error while changing the status:', err);
        } finally {
            dispatch(hideLoader());
        }
    };
    const handleConfirmStatusChange = async () => {
        if (!selectedAdmin || newStatus === null) return;
        await handleStatusToggleApi(selectedAdmin, newStatus);
        setModalOpen(false);
        setSelectedAdmin(null);
        setNewStatus(null);
    };
    const { canEdit } = usePermissions();
    const columns: GridColDef[] = [
        { field: 'username', headerName: 'Name', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        { field: 'last_logged_at', headerName: 'Last Login', flex: 0.8 },
        ...(canEdit(FunctionsEnum.MANAGE_ADMINS)
            ? [
                  {
                      field: 'status',
                      headerName: 'Status',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'center' as const,
                      align: 'center' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_ADMINS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => {
                          const isActive = params.value === userEnums.Status.active;
                          return (
                              <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                  <AntSwitch
                                      checked={isActive}
                                      disabled={!canEdit(FunctionsEnum.MANAGE_ADMINS)}
                                      onClick={e => {
                                          e.stopPropagation();
                                          handleStatusToggle(params.row);
                                      }}
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </Box>
                          );
                      },
                  },
              ]
            : []),
        ...(canEdit(FunctionsEnum.MANAGE_ADMINS)
            ? [
                  {
                      field: 'actions',
                      headerName: 'Actions',
                      sortable: false,
                      flex: 0.5,
                      headerAlign: 'right' as const,
                      align: 'right' as const,
                      cellClassName: 'cstRemovePointer',
                      renderCell: (params: GridRenderCellParams) => (
                          <Box
                              display="flex"
                              justifyContent="end"
                              columnGap={1}
                              sx={{
                                  '& .MuiIconButton-root': {
                                      padding: '8px 0px',
                                  },
                              }}
                          >
                              <IconButton onClick={e => handleEditClick(params.row, e)}>
                                  <EditOutlinedIcon
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </IconButton>
                          </Box>
                      ),
                  },
              ]
            : []),
    ];

    React.useEffect(() => {
        setAdminListData([]);
        fetchMangeAdminListData();
        if (tableRef.current) {
            tableRef.current.scrollIntoView();
        }
    }, [page, fetchMangeAdminListData]);

    return (
        <CommonTable height="calc(100vh - 200px)">
            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={listData}
                    columns={columns}
                    disableColumnMenu
                    disableColumnSorting
                    hideFooter
                    ref={tableRef}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'initial !important',
                        },
                    }}
                />
                {totalCount > 0 && (
                    <CustomPagination
                        totalCount={totalCount}
                        rowsPerPage={rowsPerPage}
                        currentPage={page}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CustomSlider
                    sliderOpen={openCreatePopUp}
                    sliderClose={() => {
                        setOpenCreatePopUp(false);
                        setIsEdit(false);
                    }}
                    drawerList={
                        <CreateAdmin
                            onClose={() => {
                                setOpenCreatePopUp(false);
                                setIsEdit(false);
                            }}
                            adminInfo={selectedAdmin}
                            fetchMangeAdminListData={fetchMangeAdminListData}
                            isEdit={isEdit}
                        />
                    }
                />
            </Paper>
            <CommonModal
                modalOpen={modalOpen}
                header={`Do you want to ${newStatus === userEnums.Status.active ? 'activate' : 'inactivate'} this admin?`}
                cancelBtnName="Cancel"
                AcceptBtnName="Yes"
                closePopup={() => setModalOpen(false)}
                acceptBtn={handleConfirmStatusChange}
            />
            <CommonModal
                modalOpen={removeAdmin}
                header={
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t('confirm_delete')}
                        </Typography>
                        <Typography variant="h6">{t('admin_delete_confirm')}</Typography>
                    </Box>
                }
                cancelBtnName="No"
                AcceptBtnName="Yes"
                closePopup={() => setRemoveAdmin(false)}
                acceptBtn={() => {}}
            />
        </CommonTable>
    );
};
